/**
 * Queries do módulo de onboarding.
 *
 * Hoje expõe apenas `useDefaultOnboardingTemplate` — busca o template
 * padrão ("Diagnóstico KM Gestão v1", slug seedado em migration 0039)
 * que Karla dispara para todo lead novo quando o switch "enviar agora"
 * está ligado no `NewLeadDialog`. Em M1-01b-ii quando entrar a tela de
 * "Configurações → Onboarding → Template padrão", este hook pode passar
 * a ler de uma tabela `app_settings` ou de um campo
 * `onboarding_templates.is_default` — a interface pública
 * (`id`, `slug`, `name_pt_br`, `published`) se mantém.
 */
import { useMutation, useQuery } from '@tanstack/react-query';
import { z } from 'zod';

import { supabase } from '../../lib/supabase.js';

/** Slug do template padrão, seedado em migration 0039. Código consome
 * por slug (não por UUID) para que a resolução sobreviva a re-seeds,
 * ambientes (dev/staging/prod) e até renaming futuro via
 * `on conflict (slug) do update`. */
export const DEFAULT_ONBOARDING_TEMPLATE_SLUG = 'diagnostico-kmgestao-v1';

/** Shape minimal que o hook devolve. Schema completo (sections,
 * questions, etc.) fica em `schema: jsonb` e só é consumido pelo
 * form-renderer da rota pública `/onboarding/:token` (M1-01b-ii) —
 * no fluxo de criar lead a UI só precisa saber que o template existe
 * e está publicado. */
export const onboardingTemplateLiteSchema = z.object({
  id: z.string().uuid(),
  slug: z.string(),
  name_pt_br: z.string(),
  version: z.number().int().positive(),
  published: z.boolean(),
});

export type OnboardingTemplateLite = z.infer<typeof onboardingTemplateLiteSchema>;

export const ONBOARDING_KEYS = {
  /** Todas as queries de onboarding. Use para invalidar em bloco em
   * handlers de submissão. */
  all: () => ['onboarding'] as const,
  /** Cache do template default — resolvido por slug. */
  defaultTemplate: () => ['onboarding', 'default-template'] as const,
};

/**
 * Busca o template padrão que será enviado por default no fluxo de
 * criar lead. Retorna `null` se não houver template publicado — nesse
 * caso o UI desabilita o switch "enviar agora" com tooltip explicativo.
 *
 * **Cache**: 5 min `staleTime`. Template raramente muda, mas um
 * PR pode republicar com `published=true/false`; 5 min mantém a UI
 * leve sem congelar o estado.
 *
 * **Segurança**: a query passa por RLS da tabela `onboarding_templates`.
 * Superadmin e service_role têm read; anon/authenticated não (RLS
 * bloqueia). A página pública de onboarding consome via edge function
 * que resolve o template pelo token da submission, não via este hook.
 */
export function useDefaultOnboardingTemplate() {
  return useQuery<OnboardingTemplateLite | null>({
    queryKey: ONBOARDING_KEYS.defaultTemplate(),
    staleTime: 5 * 60 * 1_000,
    queryFn: async () => {
      // A partir de M1-01d-0 a tabela é `form_templates` (generalizada).
      // Filtramos por `form_type='onboarding_lead'` para isolar o escopo
      // deste consumer — outros tipos (clinic_survey, patient_intake,
      // etc.) têm hooks próprios em features/surveys, features/patient-forms.
      const { data, error } = await supabase
        .from('form_templates')
        .select('id, slug, name_pt_br, version, published')
        .eq('form_type', 'onboarding_lead')
        .eq('owner_type', 'superadmin')
        .eq('slug', DEFAULT_ONBOARDING_TEMPLATE_SLUG)
        .eq('published', true)
        .maybeSingle();

      if (error) throw new Error(`Falha ao buscar template padrão: ${error.message}`);
      if (!data) return null;
      return onboardingTemplateLiteSchema.parse(data);
    },
  });
}

// ─── Self-cadastro (prospect) ────────────────────────────────────────

const createProspectFormResponseSchema = z.object({
  submission_id: z.string(),
  public_url: z.string().url(),
  token: z.string(),
  expires_at: z.string(),
  mode: z.enum(['live', 'stub']).optional(),
});

export type CreateProspectFormResponse = z.infer<typeof createProspectFormResponseSchema>;

export interface CreateProspectFormInput {
  prospect_email?: string | null;
  prospect_phone?: string | null;
  expires_in_days?: number;
}

/**
 * Mutation que gera um link público de **auto-cadastro** — fluxo
 * onde Karla encontra um médico, ganha só email/WhatsApp, e o lead
 * preenche identificação + diagnóstico sozinho. Usa a edge function
 * `create-prospect-form` (protegida por JWT Superadmin).
 *
 * Diferença vs `useSendOnboardingLink`:
 *   - Esse não requer lead cadastrado (lead é criado no submit)
 *   - Não dispara email do sistema — Karla copia a URL e envia pelo
 *     canal que quiser
 *   - Tracking (prospect_email/phone) é opcional, só pra Karla
 *     identificar a submissão depois
 */
export function useCreateProspectForm() {
  return useMutation<CreateProspectFormResponse, Error, CreateProspectFormInput>({
    mutationFn: async (input) => {
      const { data, error } = await supabase.functions.invoke('create-prospect-form', {
        body: {
          prospect_email: input.prospect_email ?? null,
          prospect_phone: input.prospect_phone ?? null,
          expires_in_days: input.expires_in_days ?? 14,
        },
      });
      if (error) throw new Error(error.message);
      return createProspectFormResponseSchema.parse(data);
    },
  });
}
